import { isEmptyString } from './string-utils'

const EXCHANGE_SHORTNAMES: Record<string, string> = {
  BINANCE: 'BN',
  OKX: 'OK',
  BYBIT: 'BY',
  DERIBIT: 'DB',
  DYDX: 'DY',
  BITFINEX: 'BF',
}

export function replaceExchangeShortname (valueToShorten: string): string {
  if (valueToShorten == null || valueToShorten.length === 0) {
    return ''
  }

  const uppercaseForm = valueToShorten.trim().toUpperCase()
  for (const key in EXCHANGE_SHORTNAMES) {
    if (uppercaseForm.includes(key)) {
      return uppercaseForm.replace(key, EXCHANGE_SHORTNAMES[key])
    }
  }
  return valueToShorten
}

/**
 * Checks that the value is in the format of ABCXYZ_EXCHANGE_TYPE
 * @param value
 */
export function isSymbolLike (value: string): boolean {
  return !isEmptyString(value) && value.includes('_') && value.split('_').length === 3
}

/**
 * Checks that the value is of type SPOT (the inverse result of this function is always SWAP)
 * @param value
 */
export function isProductTypeSpot (value: string): boolean {
  return isSymbolLike(value) && value.endsWith('_SPOT')
}

enum EXCHANGE {
  BINANCE = 'binance',
  OKX = 'okx',
  DERIBIT = 'deribit',
}

enum INFERRABLE_TYPE {
  SPOT,
  SWAP,
  FUTURE_COIN, // Binance Future Coin
  FUTURE_USD, // Binance Future USD
  FROM_PARAMS, // Determined from params
  INDETERMINATE, // Not possible to be determined
}

const EXCHANGE_VENUE_ID_TO_TYPE: Record<string, INFERRABLE_TYPE> = {
  SBN: INFERRABLE_TYPE.SPOT,
  FBNC: INFERRABLE_TYPE.FUTURE_COIN,
  FBNU: INFERRABLE_TYPE.FUTURE_USD,
  OKX: INFERRABLE_TYPE.FROM_PARAMS,
  DBIT: INFERRABLE_TYPE.INDETERMINATE,
}

/**
 * Extract INFERRABLE_TYPE from unique identifier (in format of DATA_STREAM_TYPE.VENUE_ID.#)
 * @param identifier
 */
function extractInferrableType (identifier: string): INFERRABLE_TYPE {
  try {
    const splits = identifier.split('.')
    const venueId = splits[1].toUpperCase()
    return EXCHANGE_VENUE_ID_TO_TYPE[venueId]
  } catch (e) {
    return INFERRABLE_TYPE.INDETERMINATE
  }
}

export function inferExchangeSymbol (identifier: string, params: string, uri: string): string | null {
  if (isEmptyString(uri)) {
    return null
  }

  const type = extractInferrableType(identifier)
  if (type === INFERRABLE_TYPE.INDETERMINATE) {
    return null // Not possible to be determined
  }

  const uriLowercase = uri.toLowerCase()

  // Handle exchanges that populate the inst within the params
  if (!isEmptyString(params) && type === INFERRABLE_TYPE.FROM_PARAMS) {
    const parsedParams = JSON.parse(params)
    const exchangeInst = parsedParams?.args[0]?.instId

    if (isEmptyString(exchangeInst)) {
      console.error(`Unsupported params: ${params}`)
      return null
    }

    if (uriLowercase.includes(EXCHANGE.OKX)) {
      return mapOKXInstToInternalSymbol(exchangeInst)
    } else {
      console.error(`Unsupported uri: ${uriLowercase}`)
      return null
    }
  }

  // Handle exchanges that populate the inst within the uri
  if (uriLowercase.includes(EXCHANGE.BINANCE)) {
    const inst = extractExchangeInstFromURI(EXCHANGE.BINANCE, uriLowercase)
    return mapBinanceInstToInternalSymbol(type, inst)
  }

  return null // Insufficient information to infer the symbol
}

function extractExchangeInstFromURI (exchange: EXCHANGE, uri: string): string | null {
  if (isEmptyString(uri) || uri == null) {
    return null
  }

  let regexMatch, symbol
  switch (exchange) {
    case EXCHANGE.BINANCE:
      regexMatch = uri.match('^.+\\.binance\\.com\\/ws\\/(.+)$')
      symbol = (regexMatch != null)
        ? isEmptyString(regexMatch[1])
          ? ''
          : regexMatch[1]?.split('@')[0].toUpperCase() ?? ''
        : ''
      break
    default:
      return null
  }

  return symbol
}

function mapOKXInstToInternalSymbol (exchangeSymbol: string): string | null {
  if (isEmptyString(exchangeSymbol)) {
    return null
  }

  const inst = exchangeSymbol.toUpperCase()
  const instSplits = inst.split('-')
  const lhs = instSplits[0]
  const rhs = instSplits[1]
  let symbol = null

  const type = (instSplits.length > 2) ? INFERRABLE_TYPE.SWAP : INFERRABLE_TYPE.SPOT

  try {
    switch (type) {
      case INFERRABLE_TYPE.SPOT: {
        symbol = `${lhs}${rhs}_OKX_SPOT`
        break
      }
      case INFERRABLE_TYPE.SWAP: {
        if (!inst.endsWith('SWAP')) {
          console.error(`Failed to map ${inst} as ${type}`)
          break
        }

        symbol = `${lhs}${rhs}_OKX_SWAP`
        break
      }
      default: {
        console.error(`Unsupported type: ${type}`)
        break
      }
    }
  } catch (e) {
    console.error(e)
  }

  return symbol
}

function mapBinanceInstToInternalSymbol (type: INFERRABLE_TYPE, exchangeSymbol: string | null): string | null {
  if (isEmptyString(exchangeSymbol) || exchangeSymbol == null) {
    return null
  }

  const inst = exchangeSymbol.toUpperCase()
  let symbol = null

  try {
    switch (type) {
      case INFERRABLE_TYPE.SPOT: {
        if (inst.includes('_')) {
          console.error(`Failed to map ${inst} as ${type}`)
          break
        }

        if (inst.includes('USDCUSDT')) {
          symbol = 'USDCUSDT'
        } else {
          symbol = `${inst}_BINANCE_SPOT`
        }
        break
      }
      case INFERRABLE_TYPE.FUTURE_COIN: {
        if (!inst.includes('USD')) {
          console.error(`Failed to map ${inst} as ${type}`)
          break
        }

        const splits = inst.split('_')
        if (inst.includes('_PERP')) {
          symbol = `${splits[0]}_BINANCE_SWAP`
        } else {
          symbol = `${splits[0]}_BINANCE_${splits[1]}`
        }
        break
      }
      case INFERRABLE_TYPE.FUTURE_USD: {
        if (!(inst.includes('USDC') || inst.includes('USDT'))) {
          console.error(`Failed to map ${inst} as ${type}`)
          break
        }

        const splits = inst.split('_')
        if (inst.includes('_')) {
          symbol = `${splits[0]}_BINANCE_${splits[1]}`
        } else {
          symbol = `${splits[0]}_BINANCE_SWAP`
        }
        break
      }
      default: {
        console.error(`Unsupported type: ${type}`)
        break
      }
    }
  } catch (e) {
    console.error(e)
  }

  return symbol
}
